export const REMEMBER_ENABLED_KEY = 'ncof-remember-enabled';
const REMEMBER_IDENTIFIER_KEY = 'ncof-remember-identifier';
const REMEMBER_PASSWORD_KEY = 'ncof-remember-password';
const ENCRYPTION_VERSION = 'v1';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

const REMEMBERED_KEYS = [
  REMEMBER_ENABLED_KEY,
  REMEMBER_IDENTIFIER_KEY,
  REMEMBER_PASSWORD_KEY,
] as const;

const textEncoder = () => new TextEncoder();
const textDecoder = () => new TextDecoder();

const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const base64ToBytes = (value: string): Uint8Array => {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const getCryptoKey = async (): Promise<CryptoKey> => {
  const encoder = textEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(`ncof-remember:${window.location.origin}`),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode('ncof-remember-salt-v1'),
      iterations: 100_000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
};

const encryptRememberPassword = async (password: string): Promise<string> => {
  const key = await getCryptoKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = textEncoder().encode(password);
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(ciphertext), iv.length);
  return `${ENCRYPTION_VERSION}:${bytesToBase64(combined)}`;
};

const decryptRememberPassword = async (encrypted: string): Promise<string | null> => {
  try {
    if (!encrypted.startsWith(`${ENCRYPTION_VERSION}:`)) {
      return null;
    }

    const combined = base64ToBytes(encrypted.slice(ENCRYPTION_VERSION.length + 1));
    const iv = combined.slice(0, 12);
    const ciphertext = combined.slice(12);
    const key = await getCryptoKey();
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
    return textDecoder().decode(decrypted);
  } catch {
    return null;
  }
};

const cookieSecureFlag = (): string => {
  try {
    return window.location.protocol === 'https:' ? '; Secure' : '';
  } catch {
    return '';
  }
};

export const getRememberCookie = (name: string): string | null => {
  try {
    const prefix = `${encodeURIComponent(name)}=`;
    const parts = document.cookie.split(';');
    for (const part of parts) {
      const trimmed = part.trim();
      if (trimmed.startsWith(prefix)) {
        return decodeURIComponent(trimmed.slice(prefix.length));
      }
    }
  } catch {
    return null;
  }
  return null;
};

export const setRememberCookie = (name: string, value: string): void => {
  try {
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${cookieSecureFlag()}`;
  } catch {
    // Cookies can be blocked; localStorage may still work.
  }
};

export const deleteRememberCookie = (name: string): void => {
  try {
    document.cookie = `${encodeURIComponent(name)}=; Path=/; Max-Age=0; SameSite=Lax${cookieSecureFlag()}`;
  } catch {
    // ignore
  }
};

const safeLocalStorageGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeLocalStorageSet = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
};

const safeLocalStorageRemove = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

export type RememberedStoredFields = {
  enabled: string | null;
  identifier: string | null;
  encryptedPassword: string | null;
};

export const persistRememberedFields = (fields: {
  enabled: string;
  identifier: string;
  encryptedPassword: string;
}): void => {
  safeLocalStorageSet(REMEMBER_ENABLED_KEY, fields.enabled);
  safeLocalStorageSet(REMEMBER_IDENTIFIER_KEY, fields.identifier);
  safeLocalStorageSet(REMEMBER_PASSWORD_KEY, fields.encryptedPassword);
  setRememberCookie(REMEMBER_ENABLED_KEY, fields.enabled);
  setRememberCookie(REMEMBER_IDENTIFIER_KEY, fields.identifier);
  setRememberCookie(REMEMBER_PASSWORD_KEY, fields.encryptedPassword);
};

export const readRememberedFields = (): RememberedStoredFields => {
  const fromLocalStorage: RememberedStoredFields = {
    enabled: safeLocalStorageGet(REMEMBER_ENABLED_KEY),
    identifier: safeLocalStorageGet(REMEMBER_IDENTIFIER_KEY),
    encryptedPassword: safeLocalStorageGet(REMEMBER_PASSWORD_KEY),
  };

  const fromCookies: RememberedStoredFields = {
    enabled: getRememberCookie(REMEMBER_ENABLED_KEY),
    identifier: getRememberCookie(REMEMBER_IDENTIFIER_KEY),
    encryptedPassword: getRememberCookie(REMEMBER_PASSWORD_KEY),
  };

  const merged: RememberedStoredFields = {
    enabled: fromLocalStorage.enabled || fromCookies.enabled,
    identifier: fromLocalStorage.identifier || fromCookies.identifier,
    encryptedPassword: fromLocalStorage.encryptedPassword || fromCookies.encryptedPassword,
  };

  // Rehydrate localStorage when cookies survived an in-app browser that dropped it.
  if (merged.enabled && !fromLocalStorage.enabled) {
    safeLocalStorageSet(REMEMBER_ENABLED_KEY, merged.enabled);
  }
  if (merged.identifier && !fromLocalStorage.identifier) {
    safeLocalStorageSet(REMEMBER_IDENTIFIER_KEY, merged.identifier);
  }
  if (merged.encryptedPassword && !fromLocalStorage.encryptedPassword) {
    safeLocalStorageSet(REMEMBER_PASSWORD_KEY, merged.encryptedPassword);
  }

  return merged;
};

export const isRememberMeEnabled = (): boolean => readRememberedFields().enabled === 'true';

export const clearRememberedCredentials = (): void => {
  REMEMBERED_KEYS.forEach((key) => {
    safeLocalStorageRemove(key);
    deleteRememberCookie(key);
  });
};

export type RememberedLoginState = {
  identifier: string;
  password: string | null;
};

export const loadRememberedLoginState = async (): Promise<RememberedLoginState | null> => {
  const stored = readRememberedFields();
  const enabled = stored.enabled === 'true';
  const identifier = stored.identifier?.trim() || '';
  const encryptedPassword = stored.encryptedPassword;

  if (!enabled || !identifier) {
    return null;
  }

  if (!encryptedPassword) {
    return { identifier, password: null };
  }

  const password = await decryptRememberPassword(encryptedPassword);
  if (!password) {
    clearRememberedCredentials();
    return null;
  }

  return { identifier, password };
};

export const saveRememberedCredentials = async (
  identifier: string,
  password: string
): Promise<void> => {
  const encrypted = await encryptRememberPassword(password);
  persistRememberedFields({
    enabled: 'true',
    identifier: identifier.trim(),
    encryptedPassword: encrypted,
  });
};
